import type { FeedItem } from '@/lib/feed';

/**
 * What a person is shown of the journal, and what only the audit log carries.
 *
 * The old rail printed the record raw. On a seeded fleet that meant lines like:
 *
 *   "offered T-5 offered to sam-studio: is sam.tripathi@nymbl.app's own worker,
 *    has nymbl-app available, 1/2 slots busy, about 100% window headroom left."
 *
 * Twelve of those, on the home page, above everything that had actually
 * happened. Three separate problems in one line:
 *
 * 1. **It is scheduler machinery.** An offer that expired and was retried is how
 *    the dispatcher works, not something a person acts on. The job did not move.
 * 2. **It leaks.** A colleague's email address, which laptop is theirs, how busy
 *    that laptop is and how much of their five-hour Claude window is left, to
 *    anybody who opens the page.
 * 3. **It buries.** Milestones are rare and machinery is constant, so the
 *    interesting lines are always the ones pushed off the bottom.
 *
 * So the journal is split rather than filtered. Every event is still recorded and
 * still readable -- by an admin, on the audit log, which is where a full record
 * with names and capacity in it belongs. What reaches a person's home page is the
 * milestones: the moments a job actually moved.
 *
 * This is presentation, not redaction. `lib/feed.ts` still holds every event, the
 * server still scopes what it sends (`/api/me/events` for a member, `/api/events`
 * for an admin), and the audit log renders the record verbatim.
 */

/**
 * The moments a job moved, in the sense a person would describe it. Everything
 * absent from this set is dispatcher machinery: offers, expiries, claims, holds,
 * retries, heartbeats, cooldowns, notification delivery.
 */
const MILESTONE_KINDS = new Set([
  'brief.created',
  'brief.decomposed',
  'brief.decompose_failed',
  'brief.approved',
  'brief.rejected',
  'brief.cancelled',
  'brief.checked',
  'brief.completed',
  'brief.comment',
  'task.started',
  'task.pr_opened',
  'task.review',
  'task.changes_requested',
  'task.done',
  'task.failed',
  'task.cancelled',
  'task.timed_out',
  'worker.registered',
  'worker.retired',
  'person.created',
  'person.role_changed',
  'person.removed',
  'person.password_reset',
  'helix.issue_filed',
]);

export function isMilestone(kind: string): boolean {
  return MILESTONE_KINDS.has(kind);
}

/**
 * The headline for a milestone, written here rather than taken from the event.
 *
 * The hub's `message` is an operator's sentence: complete, precise, and written
 * for the audit log, which is the right place for it. A person catching up wants
 * the verb. "Plan drafted" and "PR opened" are the whole line; what it happened
 * to goes beside it, and the operator's sentence is one click away on the
 * subject's own page.
 */
const HEADLINES: Record<string, string> = {
  'brief.created': 'Job opened',
  'brief.decomposed': 'Plan drafted',
  'brief.decompose_failed': 'Planning failed',
  'brief.approved': 'Plan approved',
  'brief.rejected': 'Job rejected',
  'brief.cancelled': 'Job cancelled',
  'brief.checked': 'Integration checked',
  'brief.completed': 'Job finished',
  'brief.comment': 'Comment',
  'task.started': 'Started',
  'task.pr_opened': 'PR opened',
  'task.review': 'Ready for review',
  'task.changes_requested': 'Sent back',
  'task.done': 'Merged',
  'task.failed': 'Failed',
  'task.cancelled': 'Cancelled',
  'task.timed_out': 'Timed out',
  'worker.registered': 'Agent enrolled',
  'worker.retired': 'Agent retired',
  'person.created': 'Member added',
  'person.role_changed': 'Role changed',
  'person.removed': 'Member removed',
  'person.password_reset': 'Temporary password set',
  'helix.issue_filed': 'Filed from Helix',
};

export function headlineFor(kind: string): string {
  return HEADLINES[kind] ?? kind.replace(/^[a-z]+\./, '').replace(/_/g, ' ');
}

/**
 * The context a milestone line carries beside its headline, split into the thing
 * it happened to and what else is worth knowing.
 *
 * Four things are cut out of the hub's sentence.
 *
 * - **Anything after a colon.** That is the operator's reasoning: the capacity
 *   arithmetic, the offer rationale, the first line of a stack. It belongs on the
 *   subject's own page and in the audit log, not in a catch-up list.
 * - **Email addresses.** The hub names people by address because that is the
 *   stable identifier. A feed anyone on the team can read should say a name or
 *   say nothing.
 * - **The verb the headline already said.** "Timed out · T-2 timed out on its
 *   worker after 45 minutes" says it twice, and the second time is the half a
 *   person's eye has to travel through to reach the part that is new.
 * - **The word "brief".** The console says job; the API, schema and database say
 *   brief, and event messages are written by the server. Copy-only, exactly as
 *   CLAUDE.md sets it out: the wire name never changes.
 *
 * The result is clipped rather than wrapped, so every line in the list is one
 * line tall and the column can be scanned straight down.
 */
export interface Detail {
  /** The task or job key, when the message leads with one. */
  key: string | null;
  /** What is left worth saying, with the headline's own verb removed. */
  rest: string;
}

export function detailFor(item: FeedItem): Detail {
  const [head] = item.message.split(/:\s/, 1);
  let text = (head ?? item.message).replace(/\S+@\S+\.\w+/g, 'someone').trim();

  // The leading key, when there is one. `T-4`, `B-xa6t`, and the en dash the
  // hub's own key formatting sometimes emits.
  let key: string | null = null;
  const leading = text.match(/^([TB][-‑–]\w+)\s+/);
  if (leading?.[1]) {
    key = leading[1].replace(/[‑–]/, '-');
    text = text.slice(leading[0].length);
  }

  // The headline's own words, when the sentence opens by repeating them. `is`
  // and `will be` come off with them so "is held" and "will be retried" do not
  // leave a dangling auxiliary behind.
  const verb = headlineFor(item.kind).toLowerCase();
  const opener = new RegExp(`^(is |was |will be |has been )?${verb}\\b[ ]?`, 'i');
  text = text.replace(opener, '');

  text = text
    .replace(/\bbriefs\b/g, 'jobs')
    .replace(/\bbrief\b/g, 'job')
    .trim();
  if (text.length > 88) text = `${text.slice(0, 87).trimEnd()}…`;
  return { key, rest: text };
}

/**
 * The milestones, newest first. Machinery is dropped rather than collapsed: a
 * folded run of holds is still a line about the dispatcher, and the home page
 * has no line to spare for one.
 */
export function milestones(items: readonly FeedItem[]): FeedItem[] {
  return items
    .filter((item) => isMilestone(item.kind))
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

/**
 * A milestone line, or a burst of them folded into one (issue #30).
 *
 * `count` is 1 for an ordinary milestone. Above that, the item stands for a
 * whole run and `headlineFor` is the wrong label for it: `burstHeadline`
 * below is what the list prints instead.
 */
export interface FeedGroup extends FeedItem {
  count: number;
}

/**
 * The kinds intake files in bulk. A schedule or the nightly adapter can open a
 * dozen jobs in the same sweep, each carrying its own `brief.created`, and a
 * person who filed one job by hand never produces a run this shape - three or
 * more of the same kind inside the window below is the signature of a batch,
 * not of someone reading each job before opening the next.
 */
const BURST_KINDS = new Set(['brief.created', 'brief.approved']);

/** How many of the same kind, back to back, before it reads as a burst rather than a coincidence. */
const BURST_MIN = 3;

/** How close together a run has to arrive to be the same burst, not two unrelated ones. */
const BURST_WINDOW_MINUTES = 5;

/**
 * Folds a run of `BURST_MIN` or more same-kind intake events, arriving within
 * `BURST_WINDOW_MINUTES` of each other, into one line.
 *
 * This is the fix for the failure the audit found: eleven jobs and their
 * approvals filed in two minutes pushed a task's own `pr_opened` line off the
 * bottom of a list sized for eight. A burst is exactly the kind of event a
 * person does not need one line per instance of - "11 jobs opened" is the
 * whole story - and folding it to one line is what buys the room for a run's
 * own milestones to stay on screen without raising the limit for everyone.
 *
 * Expects `items` newest-first, the order `milestones` already produces.
 */
export function groupIntakeBursts(items: readonly FeedItem[]): FeedGroup[] {
  const groups: FeedGroup[] = [];
  let index = 0;
  while (index < items.length) {
    const item = items[index]!;
    if (!BURST_KINDS.has(item.kind)) {
      groups.push({ ...item, count: 1 });
      index += 1;
      continue;
    }
    // Newest-first, so the run's cutoff is BURST_WINDOW_MINUTES *before* the
    // item that started it - everything still inside that window belongs to
    // the same burst.
    const cutoff = Date.parse(item.at) - BURST_WINDOW_MINUTES * 60_000;
    let end = index + 1;
    while (
      end < items.length &&
      items[end]!.kind === item.kind &&
      Date.parse(items[end]!.at) >= cutoff
    ) {
      end += 1;
    }
    const run = items.slice(index, end);
    if (run.length >= BURST_MIN) {
      groups.push({ ...item, count: run.length, fresh: run.some((one) => one.fresh) });
    } else {
      groups.push({ ...item, count: 1 });
      end = index + 1;
    }
    index = end;
  }
  return groups;
}

/** How a folded burst reads, in the same voice `headlineFor` uses for one. */
const BURST_HEADLINES: Record<string, (count: number) => string> = {
  'brief.created': (count) => `${count} jobs opened`,
  'brief.approved': (count) => `${count} plans approved`,
};

/** The headline for a `FeedGroup`: `headlineFor` for a line of one, the burst's own sentence above that. */
export function burstHeadline(item: FeedGroup): string {
  if (item.count <= 1) return headlineFor(item.kind);
  return BURST_HEADLINES[item.kind]?.(item.count) ?? `${item.count}× ${headlineFor(item.kind)}`;
}

/**
 * The colour of a milestone's mark. Narrower than `eventColor`, because this
 * list only ever draws milestones and the machinery hues would never appear.
 */
export function milestoneColor(kind: string): string {
  if (kind === 'task.done' || kind === 'brief.completed') return 'var(--dy-ok)';
  if (
    kind.endsWith('.failed') ||
    kind.endsWith('.timed_out') ||
    kind.endsWith('.cancelled') ||
    kind === 'brief.rejected'
  ) {
    return 'var(--dy-bad)';
  }
  if (kind === 'task.review' || kind === 'brief.decomposed') return 'var(--dy-gate)';
  if (kind === 'task.changes_requested') return 'var(--dy-warn)';
  if (kind.startsWith('worker.') || kind === 'task.started') return 'var(--dy-info)';
  if (kind.startsWith('brief.')) return 'var(--dy-plan)';
  return 'var(--dy-fg-3)';
}

/**
 * A plain event list, as the milestone strip reads it.
 *
 * The live feed arrives through `use-activity-feed` already in `FeedItem` shape,
 * but a page that reads events from its own endpoint -- the project rollup -- has
 * raw `DyspatchEvent`s. Both end up in the same strip, so the conversion lives
 * here rather than in whichever page needs it next.
 *
 * `fresh` is false for all of them: nothing here arrived over the stream while
 * the tab was open, so nothing should flash as new.
 */
export function feedItemsFromEvents(
  events: readonly {
    id: string;
    kind: string;
    message: string;
    actor?: string | null;
    createdAt: string;
    taskId?: string | null;
    workerId?: string | null;
    briefId?: string | null;
  }[],
): FeedItem[] {
  return events.map((event) => ({
    id: event.id,
    kind: event.kind,
    message: event.message,
    actor: event.actor ?? '',
    at: event.createdAt,
    subject: event.taskId ?? event.workerId ?? event.briefId ?? '',
    fresh: false,
  }));
}
